import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// ── Image compression utility ─────────────────────────────────────────────────
// Resizes and compresses images before upload to reduce size by ~6-8x
// Non-image files (PDFs) pass through unchanged
const compressImage = (file, maxWidthPx = 1600, quality = 0.82) => {
  return new Promise((resolve) => {
    // PDFs and non-images pass straight through
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Already small enough — skip compression
      if (img.width <= maxWidthPx && file.size < 800_000) {
        resolve(file);
        return;
      }

      const scale   = Math.min(1, maxWidthPx / img.width);
      const canvas  = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }

          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type:         'image/jpeg',
            lastModified: Date.now(),
          });

          console.log(
            `Image compressed: ${(file.size / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB`
          );
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // fallback — use original if image fails to load
    };

    img.src = url;
  });
};

// ── Main hook ─────────────────────────────────────────────────────────────────
export const useUpload = (remainingUploads) => {
  const { user, isPremium } = useAuth();
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [currentStep,  setCurrentStep]  = useState('');

  const uploadDocument = async (file, options = { isContribution: false }) => {

    // Guard: free user daily limit check
    if (!options.isContribution && !isPremium && remainingUploads <= 0) {
      toast.error('Daily limit reached. Please upgrade to Premium for unlimited uploads!');
      return null;
    }

    if (!user) {
      toast.error('Please login to continue');
      return null;
    }

    setUploading(true);
    setProgress(0);

    try {
      // ── Step 1: Compress image before uploading ───────────────────────────
      // Skipped for PDFs and contributions (past questions)
      let fileToUpload = file;

      if (!options.isContribution && file.type.startsWith('image/')) {
        setCurrentStep('Optimising image...');
        fileToUpload = await compressImage(file);
        setProgress(10);
      }

      // ── Step 2: Upload to Supabase Storage ───────────────────────────────
      setCurrentStep('Uploading file...');

      const bucketName = options.isContribution ? 'past_questions' : 'documents';

      // Always store compressed images as .jpeg for consistency
      const fileExt  = fileToUpload.type === 'image/jpeg' ? 'jpeg' : file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert:       false,
        });

      if (uploadError) throw new Error('Upload failed: ' + uploadError.message);
      setProgress(40);

      // ── Step 3: Get public URL ────────────────────────────────────────────
      setCurrentStep('Processing file paths...');
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(uploadData.path);

      setProgress(60);

      // ── Step 4: Insert DB record ──────────────────────────────────────────
      setCurrentStep('Saving record...');

      const targetTable = options.isContribution ? 'contributions' : 'documents';

      const payload = options.isContribution
        ? {
            user_id:  user.id,
            file_url: publicUrl,
            status:   'pending',
          }
        : {
            user_id:           user.id,
            file_name:         file.name,        // keep original name for display
            file_url:          publicUrl,
            file_size:         fileToUpload.size, // compressed size
            status:            'processing',
            processing_status: 'processing',
          };

      const { data: record, error: dbError } = await supabase
        .from(targetTable)
        .insert(payload)
        .select()
        .single();

      if (dbError) throw new Error('Database error: ' + dbError.message);
      setProgress(80);

      // ── Step 5: Invoke AI processing (non-contribution uploads only) ──────
      if (!options.isContribution) {
        setCurrentStep('Starting AI analysis...');

        supabase.functions.invoke('process-document', {
          body: {
            imageUrl:   publicUrl,
            documentId: record.id,
            userId:     user.id,
          },
        }).then(({ data, error }) => {
          if (error) {
            console.error('Background processing error:', error);
          } else {
            console.log('Process-document response:', data);
          }
        });

        toast.success('Analysing started! Check back in 10–20 seconds. 🎉');
      } else {
        toast.success('Thank you for your contribution! It is currently undergoing verification. 🎁');
      }

      setProgress(100);
      return record;

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Upload failed. Please try again.');
      setCurrentStep('Failed');
      return null;

    } finally {
      setUploading(false);
      setTimeout(() => {
        setProgress(0);
        setCurrentStep('');
      }, 2000);
    }
  };

  return { uploadDocument, uploading, progress, currentStep };
};